import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'
import {
  type ConnectionEvent,
  type ConnectionEventKind,
  connectionEventsAtom,
  connectionStatusAtom,
  MAX_CONNECTION_EVENTS,
} from '~/atoms/ConnectionState'
import { useOnlineStatus } from '~/hooks/useOnlineStatus'

const STATUS_TO_KIND: Record<string, ConnectionEventKind> = {
  CONNECTED: 'ws-connected',
  RECONNECTING: 'ws-reconnecting',
  SUSPENDED: 'ws-suspended',
  DISCONNECTED: 'ws-disconnected',
}

/**
 * Records connection transitions (network online/offline + realtime socket
 * status) into a short rolling log for the status panel. Diagnostic-only and
 * non-sensitive — only event kinds + timestamps, never message content.
 *
 * Mount once at an always-rendered, post-auth location (alongside the
 * heartbeat bridge). Must run inside the JotaiProvider tree so the writes land
 * in the same store the panel reads from.
 */
export function useConnectionEventLog() {
  const append = useSetAtom(connectionEventsAtom)
  const status = useAtomValue(connectionStatusAtom)
  const online = useOnlineStatus()

  // Skip the initial values so we log transitions, not the starting state.
  const prevStatus = useRef<string | null>(null)
  const prevOnline = useRef<boolean | null>(null)

  const push = useCallback(
    (kind: ConnectionEventKind) =>
      append((events) => {
        const next: ConnectionEvent = { at: Date.now(), kind }
        return [...events, next].slice(-MAX_CONNECTION_EVENTS)
      }),
    [append],
  )

  useEffect(() => {
    if (prevStatus.current !== null && prevStatus.current !== status) {
      const kind = STATUS_TO_KIND[status]
      if (kind) push(kind)
    }
    prevStatus.current = status
  }, [status, push])

  useEffect(() => {
    if (prevOnline.current !== null && prevOnline.current !== online) {
      push(online ? 'online' : 'offline')
    }
    prevOnline.current = online
  }, [online, push])
}
